/**
 * `trackframe.config.json` v1 — the on-disk track manifest (DEV-014).
 *
 * Two schemas live in this module:
 *
 *   1. `TrackframeConfigSchema` — the literal shape of the JSON file the track
 *      commits to its repo. Carries paths (`openapi`, `systemPrompt`) and an
 *      `environments` map.
 *
 *   2. `TrackManifestSchema` — the resolved payload pushed to the hub on
 *      `PUT /tracks/{id}` (HUB-062). Single-environment, inline OpenAPI object,
 *      inline `systemPrompt` text. No paths, no `environments`.
 *
 * `resolveTrackManifest` glues the two: caller reads the openapi + systemPrompt
 * files off disk (the only I/O step), then this pure helper merges in the
 * environment-specific URLs and emits a hub-ready manifest.
 *
 * Citations: HUB-062, DEV-014, DEV-007, DEV-008, DEV-009, HUB-026, COP-053.
 */
import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { JsonObjectSchema } from './common.js';
// COP-053 tool-name slug constraint. The track `name` doubles as the tool-name
// prefix (`${trackSlug}_${toolName}`) and the chat-request `trackName`, so it
// must be lowercase-kebab and short enough to leave room for the per-tool
// suffix Anthropic enforces (64-char tool-name cap).
//
// Exported so downstream validators (e.g. the hub-mcp zod tool schemas) derive
// from the same single literal instead of re-encoding it and drifting — same
// one-literal discipline as `uuidRegex` in `common.ts`.
export const TRACK_SLUG_PATTERN = '^[a-z][a-z0-9-]{1,62}$';
const TrackSlugSchema = Type.String({
    pattern: TRACK_SLUG_PATTERN,
    minLength: 2,
    maxLength: 63,
});
const UriSchema = Type.String({ format: 'uri', maxLength: 2048 });
// HUB-109 D3 / TAZ-07 — a track-declared permission name. Opaque to the hub:
// the `namespace:name:action` colon convention is never parsed. The `tool:`
// prefix is reserved for the rows the hub derives from the track's exposed
// copilot tools, so a manifest name carrying it is rejected in code
// (`validateTrackPermissionDecls`) — a negative prefix is not a TypeBox
// `pattern` the CLI, hub-client and hub could all share verbatim.
export const TRACK_PERMISSION_NAME_PATTERN = '^[a-z0-9][a-z0-9_.:-]{0,127}$';
export const TRACK_PERMISSION_RESERVED_PREFIX = 'tool:';
export const TrackPermissionDeclSchema = Type.Object({
    name: Type.String({ pattern: TRACK_PERMISSION_NAME_PATTERN, maxLength: 128 }),
    label: Type.String({ minLength: 1, maxLength: 255 }),
    description: Type.Optional(Type.String({ maxLength: 2048 })),
});
const TrackPermissionDeclsSchema = Type.Optional(Type.Array(TrackPermissionDeclSchema, { maxItems: 200 }));
/** Per-environment endpoints. `apiUrl` is the track's own API base; `baseUrl`
 *  is its public UI (rendered as a card link by the hub root); `healthCheckUrl`
 *  is optional. The hub URL the track *targets* is intentionally not declared
 *  here — that's infrastructure-level, sourced from `TF_HUB_URL` at registration
 *  time so the same committed config works across every developer's machine. */
export const TrackEnvironmentEndpointsSchema = Type.Object({
    apiUrl: UriSchema,
    baseUrl: Type.Optional(UriSchema),
    healthCheckUrl: Type.Optional(UriSchema),
});
/**
 * On-disk `trackframe.config.json` shape (DEV-014).
 *
 *   - `id` is a UUID; static; written once at scaffold time; immutable across
 *     environment promotions. The hub natural key for upsert (HUB-062).
 *   - `name` is the slug — what the agent and chat-request body use to address
 *     this track. Free to change; identity stays on `id`.
 *   - `openapi` and `systemPrompt` are paths relative to the config file.
 *     CLI / hub-client reads them and pushes the resolved content inline.
 *   - `environments` is a free-form map; deployments choose their own names
 *     (e.g. `local`, `dev`, `tenant-acme-prod`).
 */
export const TrackframeConfigSchema = Type.Object({
    schemaVersion: Type.Literal(1),
    id: Type.String({ format: 'uuid' }),
    name: TrackSlugSchema,
    displayName: Type.String({ minLength: 1, maxLength: 255 }),
    description: Type.Optional(Type.String({ maxLength: 2048 })),
    openapi: Type.String({ minLength: 1 }),
    systemPrompt: Type.Optional(Type.String({ minLength: 1 })),
    permissions: TrackPermissionDeclsSchema,
    environments: Type.Record(Type.String({ minLength: 1 }), TrackEnvironmentEndpointsSchema),
});
/**
 * Resolved manifest (`PUT /tracks/{id}` body — HUB-062).
 *
 * The hub validates the OpenAPI shape (`validateSpecShape`) and the
 * `x-trackframe-agent` annotations (HUB-026 gate) on `openapi` before
 * committing the row. `systemPrompt` is opaque text the hub stores verbatim.
 */
export const TrackManifestSchema = Type.Object({
    schemaVersion: Type.Literal(1),
    id: Type.String({ format: 'uuid' }),
    name: TrackSlugSchema,
    displayName: Type.String({ minLength: 1, maxLength: 255 }),
    description: Type.Optional(Type.String({ maxLength: 2048 })),
    apiUrl: UriSchema,
    baseUrl: Type.Optional(UriSchema),
    healthCheckUrl: Type.Optional(UriSchema),
    openapi: JsonObjectSchema,
    systemPrompt: Type.Optional(Type.String()),
    permissions: TrackPermissionDeclsSchema,
});
function toFailures(errs) {
    return Array.from(errs).map((e) => ({ path: e.path || '/', message: e.message }));
}
/**
 * The two `permissions[]` rules the JSON schema cannot express (HUB-109 D3):
 * no manifest name may start with the reserved `tool:` prefix (those rows are
 * derived by the hub from the exposed copilot tools, so the two kinds never
 * collide), and names are unique within one manifest. Pure. Shared by the
 * on-disk and wire validators so the CLI / hub-client refuse locally with the
 * same code the hub answers with (422).
 */
export function validateTrackPermissionDecls(decls) {
    const seen = new Set();
    for (const { name } of decls) {
        if (name.startsWith(TRACK_PERMISSION_RESERVED_PREFIX)) {
            return {
                ok: false,
                code: 'invalid_permission_name',
                message: `Permission name '${name}' uses the reserved '${TRACK_PERMISSION_RESERVED_PREFIX}' prefix (hub-derived tool permissions)`,
            };
        }
        if (seen.has(name)) {
            return {
                ok: false,
                code: 'duplicate_permission_name',
                message: `Permission name '${name}' is declared more than once`,
            };
        }
        seen.add(name);
    }
    return { ok: true };
}
function permissionDeclsFailure(decls) {
    const result = validateTrackPermissionDecls(decls ?? []);
    return result.ok ? null : [{ path: '/permissions', message: result.message }];
}
/**
 * Validate an in-memory `trackframe.config.json` against the v1 schema.
 *
 * Pure: no file I/O. The caller is responsible for reading + JSON.parse'ing
 * the file before invoking this validator. Returns a tagged union so callers
 * can branch without try/catch.
 */
export function validateTrackframeConfig(input) {
    if (!Value.Check(TrackframeConfigSchema, input)) {
        return { ok: false, errors: toFailures(Value.Errors(TrackframeConfigSchema, input)) };
    }
    // The on-disk schema permits an empty environments map at the JSON-Schema
    // level (TypeBox `Type.Record` has no `minProperties` knob); enforce ≥1 here
    // so the validator surfaces it as a single clear error rather than letting
    // `resolveTrackManifest` blow up later with "environment not found".
    const cfg = input;
    if (Object.keys(cfg.environments).length === 0) {
        return {
            ok: false,
            errors: [{ path: '/environments', message: 'environments must declare at least one entry' }],
        };
    }
    const permissionErrors = permissionDeclsFailure(cfg.permissions);
    if (permissionErrors)
        return { ok: false, errors: permissionErrors };
    return { ok: true, value: cfg };
}
/**
 * Validate a resolved `TrackManifest` (the body of `PUT /tracks/{id}`).
 *
 * Run at the seam between hub-client/CLI and the hub: hub-client validates
 * before send, hub validates on receipt. The OpenAPI shape and the
 * `x-trackframe-agent` annotation gate (HUB-026) are checked by the hub route
 * handler against the dereferenced `openapi` object — not this validator.
 */
export function validateTrackManifest(input) {
    if (!Value.Check(TrackManifestSchema, input)) {
        return { ok: false, errors: toFailures(Value.Errors(TrackManifestSchema, input)) };
    }
    const manifest = input;
    const permissionErrors = permissionDeclsFailure(manifest.permissions);
    if (permissionErrors)
        return { ok: false, errors: permissionErrors };
    return { ok: true, value: manifest };
}
/**
 * Resolve `(config, environment, fileContents) → TrackManifest`.
 *
 * Pure: no file I/O. Throws `Error` if the requested environment is absent
 * from `config.environments` — that's a programmer/operator error, not a
 * field-shape problem `validateTrackframeConfig` could have caught.
 */
export function resolveTrackManifest(config, environment, inputs) {
    const env = config.environments[environment];
    if (!env) {
        const known = Object.keys(config.environments).join(', ') || '(none)';
        throw new Error(`Environment '${environment}' not found in trackframe.config.json. Known environments: ${known}`);
    }
    const manifest = {
        schemaVersion: 1,
        id: config.id,
        name: config.name,
        displayName: config.displayName,
        apiUrl: env.apiUrl,
        openapi: inputs.openapi,
    };
    if (config.description !== undefined)
        manifest.description = config.description;
    if (env.baseUrl !== undefined)
        manifest.baseUrl = env.baseUrl;
    if (env.healthCheckUrl !== undefined)
        manifest.healthCheckUrl = env.healthCheckUrl;
    if (inputs.systemPrompt !== undefined)
        manifest.systemPrompt = inputs.systemPrompt;
    if (config.permissions !== undefined)
        manifest.permissions = config.permissions;
    return manifest;
}
//# sourceMappingURL=trackframe-config.js.map